import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { DatePipe } from '@angular/common';
import {
    GridOptions,
    ICellRenderer,
    ICellRendererParams,
    RowNode,
} from "ag-grid-community";
import { ModuleManager } from "src/shared/models/module-manager";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { AgGridHelperService } from "src/app/reusables/services/ag-grid-helper.service";
import { TasksService, TasksSharedData } from "./tasks.service";
import { GetTaskApiModel, LoginDTO, TasksRowNode } from "./tasks.models";
import OverlayScrollbars from 'overlayscrollbars';
import { AgGridAngular } from "ag-grid-angular";

@Component({
    selector: "cv-tasks",
    templateUrl: "./tasks.component.html",
    styleUrls: ["./tasks.component.scss"],
})
export class TasksComponent implements OnInit {

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild('tasksToDoGrid') agGridToDo: AgGridAngular;
    @ViewChild('tasksSentGrid') agGridSent: AgGridAngular;
    @ViewChild('tasksCompletedGrid') agGridCompleted: AgGridAngular;

    private isDataLoading = false;
    private isFirstLoad = true;
    private stateSaveTimeout: any; // For debouncing calls to queueStateSave().
    public ts: TasksSharedData;
    public data: GetTaskApiModel = null;
    public isFirstDataLoaded = false;  // to avoid showing the grid before the data is loaded.
    public filterValue = "";
    public showFilter = false;
    public showToDo = true;
    public confirmationDialogOpen = false;
    public completeConfirm = true; // True means that the confirmation dialog is for completing a task, false means that the confirmation dialog is for deleting a task
    public confirmationTaskId: number;
    public addMode = false;
    public editMode = false;
    public saving = false;
    public scrollbar: Object = null;
    public selectedLogins: LoginDTO[] = [];
    public selectedLoginIDs: number[] = [];
    public namePristine = true;
    public dateError = "";
    public cvAngularDateFormat: string = window["CVAngularDateFormat"];
    public cvAngularTimeFormat: string = window["CVAngularTimeFormat"];
    public gridReady = false;
    public gridOptionsToDo: GridOptions = null;
    public gridOptionsSent: GridOptions = null;
    public gridOptionsCompleted: GridOptions = null;
    public activeTabIndex: number = 0;
    public scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "os-theme-dark",
        },
    };

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private tasksService: TasksService,
        private datePipe: DatePipe,
    ) {
        this.ts = this.tasksService.dataObj;
    }

    prepGrid() {
        this.gridOptionsToDo = this.agGridHelperService.createGridOptions(
            {
                columnDefs: [
                    {
                        checkboxSelection: (params) => params.data.Signoff,
                        sortable: false,
                        menuTabs: [],
                        maxWidth: 46,
                    },
                    {
                        field: "TaskName",
                        colId: "TaskNameToDo",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Tasks",
                            "Tasks"
                        ),
                        cellRenderer: TaskNameRenderer,
                        menuTabs: [],
                    },
                    {
                        field: "DueDate",
                        colId: "DueDateToDo",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Due_Date",
                            "Due Date"
                        ),
                        valueGetter: (params) => {
                            let value = params.data[params.colDef.field];
                            return value
                                ? this.datePipe.transform(value, CVAngularDateTimeFormat)
                                : this.cvLocalizationService.getCachedText(
                                    "Tasks",
                                    "No_Due_Date",
                                    "No Due Date"
                                );
                        },
                        comparator: (
                            dateA,
                            dateB,
                            nodeA: RowNode,
                            nodeB: RowNode,
                            reverse: boolean
                        ) => this.compareDates(nodeA.data.DueDate, nodeB.data.DueDate),
                        sort: "asc",
                        menuTabs: [],
                        maxWidth: 156,
                    },
                    {
                        field: "Assigner",
                        colId: "AssignerToDo",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Assigned_By",
                            "Assigned By"
                        ),
                        cellRenderer: (params) => {
                            var cellContents = `<div class="d-flex justify-content-between align-items-center">${params.value}`;
                            cellContents +=
                                params.data.TaskType == 3
                                    ? `<div class="taskCoachingIcon"></div></div>`
                                    : `</div>`;
                            return cellContents;
                        },
                        menuTabs: [],
                    },
                ],
                onGridReady: (params) => {
                    if (this.data && this.data.AssignedTable)
                        params.api.setGridOption('rowData', this.data.AssignedTable);
                    params.api.sizeColumnsToFit();
                },
                getRowId: ({ data: rowData }) => rowData.ID,
                isExternalFilterPresent: () => this.filterValue.trim().length > 0,
                doesExternalFilterPass: (node) =>
                    this.agGridHelperService.doesExternalFilterPass(
                        node,
                        this.filterValue.trim()
                    ),
                onSortChanged: (event) => this.queueStateSave(),
                onColumnMoved: (event) => this.queueStateSave(),
                onColumnPinned: (event) => this.queueStateSave(),
                onGridSizeChanged: (gridEvent) => {
                    if (gridEvent.clientWidth > 0)
                        gridEvent.api.sizeColumnsToFit();
                },
                onColumnVisible: (event) => {
                    event.api.sizeColumnsToFit();
                    this.queueStateSave();
                },
                onRowSelected: (params) => {
                    if (params.node.isSelected())
                        this.openConfirmationDialog(params.data.ID, true);
                },
                suppressRowClickSelection: true,
                suppressCellFocus: true,
                suppressMovableColumns: true,
                context: {
                    openConfirmationDialog:
                        this.openConfirmationDialog.bind(this),
                    openEditMenu: this.openEditMenu.bind(this),
                },
            },
            {
                enableCopy: true,
            }
        );

        this.gridOptionsSent = this.agGridHelperService.createGridOptions(
            {
                columnDefs: [
                    {
                        checkboxSelection: true,
                        sortable: false,
                        menuTabs: [],
                        maxWidth: 46,
                    },
                    {
                        field: "TaskName",
                        colId: "TaskNameSent",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Tasks",
                            "Tasks"
                        ),
                        cellRenderer: TaskNameRenderer,
                        menuTabs: [],
                    },
                    {
                        field: "DueDate",
                        colId: "DueDateSent",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Due_Date",
                            "Due Date"
                        ),
                        valueGetter: (params) => {
                            let value = params.data[params.colDef.field];
                            return value
                                ? this.datePipe.transform(value, CVAngularDateTimeFormat)
                                : this.cvLocalizationService.getCachedText(
                                    "Tasks",
                                    "No_Due_Date",
                                    "No Due Date"
                                );
                        },
                        comparator: (
                            dateA,
                            dateB,
                            nodeA: RowNode,
                            nodeB: RowNode,
                            reverse: boolean
                        ) => this.compareDates(nodeA.data.DueDate, nodeB.data.DueDate),
                        sort: "asc",
                        menuTabs: [],
                        maxWidth: 156,
                    },
                    {
                        field: "Assignee",
                        colId: "AssigneeSent",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Assigned_To",
                            "Assigned To"
                        ),
                        cellRenderer: (params) => {
                            var cellContents = `<div class="d-flex justify-content-between align-items-center">${params.value}`;
                            cellContents +=
                                params.data.TaskType == 3
                                    ? `<div class="taskCoachingIcon"></div></div>`
                                    : `</div>`;
                            return cellContents;
                        },
                        menuTabs: [],
                    },
                ],
                onGridReady: (params) => {
                    if (this.data && this.data.SentTable)
                        params.api.setGridOption('rowData', this.data.SentTable);
                    params.api.sizeColumnsToFit();
                },
                getRowId: ({ data: rowData }) => rowData.ID,
                isExternalFilterPresent: () => this.filterValue.length > 0,
                doesExternalFilterPass: (node) =>
                    this.agGridHelperService.doesExternalFilterPass(
                        node,
                        this.filterValue
                    ),
                onSortChanged: (event) => this.queueStateSave(),
                onColumnMoved: (event) => this.queueStateSave(),
                onColumnPinned: (event) => this.queueStateSave(),
                onGridSizeChanged: (gridEvent) => {
                    if (gridEvent.clientWidth > 0)
                        gridEvent.api.sizeColumnsToFit();
                },
                onColumnVisible: (event) => {
                    event.api.sizeColumnsToFit();
                    this.queueStateSave();
                },
                onRowSelected: (params) => {
                    if (params.node.isSelected())
                        this.openConfirmationDialog(params.data.ID, true);
                },
                suppressRowClickSelection: true,
                suppressCellFocus: true,
                suppressMovableColumns: true,
                context: {
                    openConfirmationDialog:
                        this.openConfirmationDialog.bind(this),
                    openEditMenu: this.openEditMenu.bind(this),
                },
            },
            {
                enableCopy: true,
            }
        );

        this.gridOptionsCompleted = this.agGridHelperService.createGridOptions(
            {
                columnDefs: [
                    {
                        field: "TaskName",
                        colId: "TasksCompleted",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Tasks",
                            "Tasks"
                        ),
                        menuTabs: [],
                    },
                    {
                        field: "CompletedDate",
                        colId: "CompletedDate",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Date_Completed",
                            "Date Completed"
                        ),
                        valueGetter: (params) => {
                            const value = params.data[params.colDef.field];
                            return this.datePipe.transform(value, CVAngularDateTimeFormat);
                        },
                        cellStyle: (params) => {
                            if(params.data.DueDate)
                                return this.getCellColor(params);
                        },
                        comparator: (
                            dateA,
                            dateB,
                            nodeA: RowNode,
                            nodeB: RowNode,
                            reverse: boolean
                        ) => this.compareDates(nodeA.data.CompletedDate, nodeB.data.CompletedDate),
                        sort: "asc",
                        menuTabs: [],
                        maxWidth: 156,
                    },
                    {
                        field: "Assigner",
                        colId: "AssignerCompleted",
                        headerName: this.cvLocalizationService.getCachedText(
                            "Tasks",
                            "Assigned_By",
                            "Assigned By"
                        ),
                        cellRenderer: (params) => {
                            let cellContents = `<div class="d-flex justify-content-between align-items-center">${params.value}`;
                            cellContents +=
                                params.data.TaskType === 3
                                    ? `<div class="taskCoachingIcon"></div></div>`
                                    : `</div>`;
                            return cellContents;
                        },
                        menuTabs: [],
                    },
                ],
                onGridReady: (params) => {
                    if (this.data && this.data.CompletedTable)
                        params.api.setGridOption('rowData', this.data.CompletedTable);
                    params.api.sizeColumnsToFit();
                },
                getRowId: ({ data: rowData }) => rowData.ID,
                isExternalFilterPresent: () => this.filterValue.trim().length > 0,
                doesExternalFilterPass: (node) =>
                    this.agGridHelperService.doesExternalFilterPass(
                        node,
                        this.filterValue.trim()
                    ),
                onSortChanged: (event) => this.queueStateSave(),
                onColumnMoved: (event) => this.queueStateSave(),
                onColumnPinned: (event) => this.queueStateSave(),
                onGridSizeChanged: (event) => {
                    if (event.clientWidth > 0)
                        event.api.sizeColumnsToFit();
                },
                onColumnVisible: (event) => {
                    event.api.sizeColumnsToFit();
                    this.queueStateSave();
                },
                suppressRowClickSelection: true,
                suppressCellFocus: true,
                suppressMovableColumns: true,
            },
            {
                enableCopy: true,
            }
        );


        this.gridReady = true;
    }

    async ngOnInit() {
        // Bound values ("@Input()" above) are only valid in and after this function.
        // Show the loading spinner.  This will be unset in the loadData() function:
        this.moduleManager.showLoader = true;
        await this.cvLocalizationService.GetSection("Tasks");

        // Module Configuration:
        this.moduleManager.setOptions({
            //name: '' // sets the title of the module (defaults to the database entry ModuleName if unset).
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: (settings) => {
                // this function will be called when the settings are loaded for the first time (for existing modules)
                //  and also when the user clicks the apply button in the settings modal
                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true, // hasRefresh determines if a module has a refresh menu item, this item will call onRefresh when clicked
            // in general refreshType should not be changed, it will be determined automatically based upon metrics you have.
            // if it does need to be changed, see ModuleContainer.js -> moduleOptions object for more information
            refreshType: ModuleManagerRefreshType.FiveMinute,
            onRefresh: (isAutoRefresh) => {
                // isAutoRefresh is set to true when the timer is causing the reload.  It's false when the user clicks the refresh button themself.
                // this function will be called when the refresh interval for this module is hit
                // this can be called before the module is ready for it, so any checking to determine if
                //  it is actually ready to refresh should be done here
                // example:
                // if (this.dataLoaded)
                //    loadData(); // load data again
                // note: loadData() should have code to make sure we don't show a loading animation and instead just update the data we already have
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            canRefresh: () =>
                !this.isDataLoading && !this.addMode && !this.editMode, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
        });

        this.prepGrid();
        //#region Menu Items

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        //#endregion

        //#region States and Settings
        // States - used for saving values that are not set in settings
        //  example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "sortedColumn",
            getStateData: () => {
                //return this.sortedColumn;
            },
            onStateLoaded: (stateData) => {
                //this.sortedColumn = stateData;
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState('sortedColumn');
            autoLoad: true,
        });
        // Settings - used for adding custom settings to the settings dialog
        //  requires a [Template]Settings directive (see _TemplateSettings.js/aspx)
        //#endregion
    }

    public applyFilter() {
        if (this.agGridToDo?.api) {
            this.agGridToDo.api.onFilterChanged();
        }
        if (this.agGridSent?.api) {
            this.agGridSent.api.onFilterChanged();
        }
        if (this.agGridCompleted?.api) {
            this.agGridCompleted.api.onFilterChanged();
        }
    }

    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    public toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
            setTimeout(function () {
                (
                    document
                        .getElementsByClassName(`tasksFilterInput`)
                        .item(0) as HTMLFormElement
                ).focus();
            }, 0);
        }
    }

    public async loadData(isAutoRefresh: boolean) {
        if (this.isDataLoading) return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh) this.moduleManager.showLoader = true;

        try {
            this.data = await this.tasksService.loadTasksData(
                this.moduleKey,
                isAutoRefresh,
                this.isFirstLoad
            );

            if (this.agGridToDo && this.agGridToDo.api) {
                this.agGridToDo.api.setGridOption('rowData', this.data.AssignedTable);
            } // else, the grid's onGridReady() event handler should load up its initial values.

            if (this.agGridSent && this.agGridSent.api) {
                this.agGridSent.api.setGridOption('rowData', this.data.SentTable);
            } // else, the grid's onGridReady() event handler should load up its initial values.

            if(this.agGridCompleted && this.agGridCompleted.api) {
                this.agGridCompleted.api.setGridOption('rowData', this.data.CompletedTable);
            } // else, the grid's onGridReady() event handler should load up its initial values.

            this.isFirstDataLoaded = true;
            this.isFirstLoad = false;
            this.moduleManager.showLoader = false;

            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
            console.log(err);
        } finally {
            this.isDataLoading = false;
        }
    }

    public openConfirmationDialog(taskId: number, forComplete: boolean) {
        this.confirmationTaskId = taskId;
        this.completeConfirm = forComplete;
        this.confirmationDialogOpen = true;
    }

    public closeConfirmationDialog() {
        this.confirmationDialogOpen = false;
        if (this.agGridToDo?.api) this.agGridToDo.api.deselectAll();
        if (this.agGridSent?.api) this.agGridSent.api.deselectAll();
    }

    public async completeTask() {
        this.confirmationDialogOpen = false;
        await this.tasksService.completeTask(this.confirmationTaskId);
        await this.loadData(false);
    }

    public async deleteTask() {
        this.confirmationDialogOpen = false;
        await this.tasksService.deleteTask(this.confirmationTaskId);
        await this.loadData(false);
    }

    public openAddMenu() {
        this.addMode = true;
        this.validateDates();
        this.moduleManager.setAutoRefreshReady(false);
    }

    public openEditMenu(task: TasksRowNode) {
        this.editMode = true;
        this.namePristine = false;
        this.ts.editTaskId = task.ID;
        this.ts.save = {
            Name: task.TaskName,
            StartDate: new Date(task.StartDate),
            DueDate: task.DueDate ? new Date(task.DueDate) : null,
            AssignToSelf: true, // This will prevent the signoff checkbox from appearing. This value, along with Assignees and Signoff, will not be sent to the server on edit, so they can just be whatever
            Assignees: [],
            Signoff: true,
        };
        this.validateDates();
        this.moduleManager.setAutoRefreshReady(false);
    }

    public async addTask() {
        this.saving = true;
        this.ts.save.Assignees = this.selectedLogins.map(
            (login) => login.LoginID
        );
        if (this.ts.save.StartDate == null)
            this.ts.save.StartDate = new Date(Date.today());
        await this.tasksService.addTask();
        await this.loadData(false);
        this.closeAddEditMenu();
    }

    public async editTask() {
        this.saving = true;
        await this.tasksService.editTask();
        await this.loadData(false);
        this.closeAddEditMenu();
    }

    public closeAddEditMenu() {
        this.addMode = false;
        this.editMode = false;
        this.namePristine = true;
        this.ts.save = {
            Name: "",
            StartDate: new Date(Date.today()),
            DueDate: null,
            AssignToSelf: true,
            Assignees: [],
            Signoff: true,
        };
        this.saving = false;
        this.selectedLoginIDs = [];
        this.selectedLogins = [];
        this.moduleManager.setAutoRefreshReady();
    }

    onSelectionChanged(event) {
        //Select All Twice
        const selectedAllTwice = this.selectedLoginIDs.length === event.value.length;
        if(selectedAllTwice)
        {
            this.selectedLoginIDs = [];
            this.selectedLogins = [];
            return;
        }
        this.selectedLoginIDs = event.value;
        this.selectedLogins = this.data.AvailableLogins.filter(m => this.selectedLoginIDs.includes(m.LoginID));

    }

    public validateDates() {
        if (this.ts.save.DueDate == null) this.dateError = "";
        else if (new Date(Date.now()).compareTo(this.ts.save.DueDate) >= 0)
            this.dateError = this.cvLocalizationService.getCachedText(
                "Tasks",
                "Past_Due_Date",
                "Past Due Date"
            );
        else if (
            new Date(this.ts.save.StartDate).compareTo(this.ts.save.DueDate) >=
            0
        )
            this.dateError = this.cvLocalizationService.getCachedText(
                "Tasks",
                "Invalid_Due_Date",
                "Invalid Due Date"
            );
        else this.dateError = "";
    }

    public compareDates(date1: Date, date2: Date) {
        if (!date1 && !date2)
            return 0;
        else if (!date1 && date2)
            return 1;
        else if (date1 && !date2)
            return -1;
        return new Date(date1).compareTo(
            new Date(date2)
        );
    }

    public getCellColor(params) {
        const result =  this.compareDates(params.data.CompletedDate, params.data.DueDate);

        if(result >= 1)
            return { color: '#BD2026' };
    };

    getLocalizedString(stringToLocalize : string = "Type_Name_Here")
    {
        return this.cvLocalizationService.getCachedText("Tasks", stringToLocalize);
    }

    private queueStateSave() {
        if (this.stateSaveTimeout) clearTimeout(this.stateSaveTimeout);

        this.stateSaveTimeout = setTimeout(async () => {
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
            this.stateSaveTimeout = null;
        }, 500);
    }
}

class TaskNameRenderer implements ICellRenderer {
    element: HTMLDivElement;

    init(params: ICellRendererParams) {
        this.element = document.createElement("div");
        if (params.value) {
            $(this.element).addClass(
                "d-flex justify-content-between align-items-center taskEditDelete"
            );
            this.element.innerHTML =
                `<div class="text-truncate">${params.value}</div>` +
                `<div style="visibility:hidden; width:0px">` +
                `<button type="button" class="icon-btn fill-valyrianSteel fill-hover-whaleTail">` +
                `<svg class="icon-16"><use href="../App_Themes/clearview-default/icons/utility/delete.svg#delete"></svg>` +
                `</button>` +
                `<button type="button" class="icon-btn fill-valyrianSteel fill-hover-whaleTail">` +
                `<svg class="icon-16"><use href="../App_Themes/clearview-default/icons/utility/edit.svg#edit"></svg>` +
                `</button>` +
                `</div>`;
            if (
                params.colDef.colId == "TaskNameToDo" &&
                params.data.AssignerID != params.data.AssigneeID
            )
                $(this.element).find("div")[1].remove();
            else {
                $(this.element)
                    .find("button")[0]
                    .addEventListener("click", () =>
                        params.context.openConfirmationDialog(
                            params.data.ID,
                            false
                        )
                    );
                $(this.element)
                    .find("button")[1]
                    .addEventListener("click", () =>
                        params.context.openEditMenu(params.data)
                    );
            }
        }
    }
    getGui() {
        return this.element;
    }

    refresh() {
        return false;
    }
}
